import { useEffect, useRef, useState } from 'react';
import { useCelebrationStore, CelebrationType } from '../../stores/celebrationStore';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  alpha: number;
  decay: number;
}

const RANK_CONFIG: Record<Exclude<CelebrationType, 'none'>, {
  colors: string[];
  particleCount: number;
  glowColor: string;
  textColor: string;
  shadowColor: string;
}> = {
  rank1: {
    colors: ['#FFD700', '#FFA500', '#FFEC8B', '#F0E68C', '#DAA520'],
    particleCount: 150,
    glowColor: 'rgba(255, 215, 0, 0.15)',
    textColor: 'text-yellow-400',
    shadowColor: 'rgba(255,215,0,0.8)',
  },
  rank2: {
    colors: ['#C0C0C0', '#E8E8E8', '#A9A9A9', '#D3D3D3', '#FFFFFF'],
    particleCount: 120,
    glowColor: 'rgba(192, 192, 192, 0.12)',
    textColor: 'text-gray-300',
    shadowColor: 'rgba(192,192,192,0.7)',
  },
  rank3: {
    colors: ['#CD7F32', '#B87333', '#DA9100', '#C9A66B', '#A0522D'],
    particleCount: 100,
    glowColor: 'rgba(205, 127, 50, 0.1)',
    textColor: 'text-orange-400',
    shadowColor: 'rgba(205,127,50,0.6)',
  },
};

export default function CelebrationOverlay() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);
  const { activeCelebration, clearCelebration } = useCelebrationStore();
  const [showText, setShowText] = useState(false);
  const [textVisible, setTextVisible] = useState(false);

  // Create particles when celebration starts
  useEffect(() => {
    if (activeCelebration === 'none') {
      setShowText(false);
      setTextVisible(false);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const config = RANK_CONFIG[activeCelebration];
    const { colors, particleCount } = config;

    // Create explosion of particles from center
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    const newParticles: Particle[] = [];
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.5;
      const speed = 3 + Math.random() * 8;

      newParticles.push({
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2,
        size: 3 + Math.random() * 5,
        color: colors[Math.floor(Math.random() * colors.length)]!,
        alpha: 1,
        decay: 0.015 + Math.random() * 0.01,
      });
    }

    particlesRef.current = newParticles;

    // Show text after a short delay
    setTimeout(() => {
      setShowText(true);
      setTimeout(() => setTextVisible(true), 50);
    }, 200);

    // Clear celebration after animation
    const timeout = setTimeout(() => {
      clearCelebration();
    }, 3000);

    return () => clearTimeout(timeout);
  }, [activeCelebration, clearCelebration]);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw edge glow based on rank
      if (activeCelebration !== 'none') {
        const config = RANK_CONFIG[activeCelebration];
        const gradient = ctx.createRadialGradient(
          canvas.width / 2,
          canvas.height / 2,
          Math.min(canvas.width, canvas.height) * 0.3,
          canvas.width / 2,
          canvas.height / 2,
          Math.min(canvas.width, canvas.height) * 0.7
        );
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        gradient.addColorStop(1, config.glowColor);
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Update and draw particles
      particlesRef.current = particlesRef.current.filter((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.15;
        p.alpha -= p.decay;

        if (p.alpha <= 0) return false;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
        ctx.globalAlpha = 1;

        return true;
      });

      if (particlesRef.current.length > 0 || activeCelebration !== 'none') {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animate();

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [activeCelebration]);

  if (activeCelebration === 'none') return null;

  const config = RANK_CONFIG[activeCelebration];
  const rankNumber = activeCelebration === 'rank1' ? '1' : activeCelebration === 'rank2' ? '2' : '3';

  return (
    <div className="absolute inset-0 pointer-events-none z-40">
      {/* Particle canvas */}
      <canvas
        ref={canvasRef}
        width={800}
        height={600}
        className="absolute inset-0 w-full h-full"
      />

      {/* Celebration text */}
      {showText && (
        <div
          className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${
            textVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
          }`}
        >
          <div
            className={`text-center ${config.textColor}`}
            style={{ filter: `drop-shadow(0 0 20px ${config.shadowColor})` }}
          >
            <div className={`text-5xl font-bold mb-2 ${activeCelebration === 'rank1' ? 'animate-pulse' : ''}`}>
              #{rankNumber}
            </div>
            <div className="text-xl font-semibold tracking-wider">
              {activeCelebration === 'rank1' && 'TOP SCORE'}
              {activeCelebration === 'rank2' && 'SECOND PLACE'}
              {activeCelebration === 'rank3' && 'THIRD PLACE'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
